import { useState, useRef, useCallback } from "react";

// ─── Data ────────────────────────────────────────────────────────────────────
const CLASSIFICATIONS = ["OFFICIAL", "OFFICIAL: Sensitive", "PROTECTED"];
const CATEGORIES = ["HR & People", "Security & PSPF", "ICT Policy", "Finance", "Privacy & APP", "WHS", "Procurement", "Onboarding"];

const INITIAL_DOCS = [
  { id: 1, title: "APS Code of Conduct", version: "v4.2", owner: "APSC", classification: "OFFICIAL", category: "HR & People", updated: "2024-03-15", status: "Current", size: "2.4 MB" },
  { id: 2, title: "Protective Security Policy Framework", version: "v2024.1", owner: "AGD", classification: "OFFICIAL: Sensitive", category: "Security & PSPF", updated: "2024-01-10", status: "Current", size: "5.1 MB" },
  { id: 3, title: "Information Security Manual", version: "v2024.2", owner: "ASD", classification: "OFFICIAL: Sensitive", category: "ICT Policy", updated: "2024-02-28", status: "Current", size: "8.7 MB" },
  { id: 4, title: "Commonwealth Procurement Rules", version: "v5.1", owner: "Finance", classification: "OFFICIAL", category: "Procurement", updated: "2024-01-05", status: "Current", size: "1.9 MB" },
  { id: 5, title: "Privacy & APP Policy", version: "v3.1", owner: "OAIC", classification: "OFFICIAL", category: "Privacy & APP", updated: "2023-12-01", status: "Current", size: "1.2 MB" },
  { id: 6, title: "APS Onboarding Guidelines", version: "v2.3", owner: "APSC", classification: "OFFICIAL", category: "Onboarding", updated: "2024-02-14", status: "Current", size: "0.8 MB" },
  { id: 7, title: "Workplace Diversity Framework", version: "v3.0", owner: "APSC", classification: "OFFICIAL", category: "HR & People", updated: "2023-11-20", status: "Current", size: "1.5 MB" },
  { id: 8, title: "WHS Policy", version: "v4.0", owner: "SES", classification: "OFFICIAL", category: "WHS", updated: "2024-01-22", status: "Current", size: "2.1 MB" },
];

const FOLDER_TREE = [
  { id: "f1", name: "HR & People", icon: "👥", children: ["APS Code of Conduct v4.2.pdf", "Workplace Diversity v3.0.pdf", "Performance Framework.pdf"], expanded: true },
  { id: "f2", name: "Security", icon: "🔒", children: ["PSPF 2024.pdf", "ISM 2024.pdf", "Security Incident Response.pdf"], expanded: false },
  { id: "f3", name: "ICT", icon: "💻", children: ["ICT Acceptable Use Policy.pdf", "Cloud Strategy 2024.pdf"], expanded: false },
  { id: "f4", name: "Finance", icon: "💼", children: ["Procurement Rules v5.1.pdf", "Budget Guidelines.pdf", "Travel Policy.pdf"], expanded: false },
  { id: "f5", name: "Privacy", icon: "🛡️", children: ["Privacy APP Policy v3.1.pdf", "Data Handling Procedures.pdf"], expanded: false },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const classColor = (c) => {
  if (c === "PROTECTED") return { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" };
  if (c === "OFFICIAL: Sensitive") return { bg: "#fff7ed", text: "#92400e", border: "#fed7aa" };
  return { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" };
};

const ClassBadge = ({ c }) => {
  const col = classColor(c);
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 4, background: col.bg, color: col.text, border: `1px solid ${col.border}`, whiteSpace: "nowrap", fontFamily: "'DM Mono', monospace" }}>
      {c}
    </span>
  );
};

// ─── Page 1: Document Dashboard ──────────────────────────────────────────────
function DocumentDashboard({ docs, setDocs }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [filterClass, setFilterClass] = useState("All");
  const [editingId, setEditingId] = useState(null);
  const [editBuf, setEditBuf] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const filtered = docs.filter(d => {
    const matchSearch = d.title.toLowerCase().includes(search.toLowerCase()) || d.owner.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === "All" || d.category === filterCat;
    const matchClass = filterClass === "All" || d.classification === filterClass;
    return matchSearch && matchCat && matchClass;
  });

  const startEdit = (doc) => { setEditingId(doc.id); setEditBuf({ ...doc }); };
  const saveEdit = () => {
    setDocs(docs.map(d => d.id === editingId ? { ...editBuf } : d));
    setEditingId(null);
    showToast("Document updated successfully");
  };
  const archiveDoc = (id) => {
    setDocs(docs.map(d => d.id === id ? { ...d, status: "Archived" } : d));
    showToast("Document archived", "warning");
  };
  const deleteDoc = (id) => {
    setDocs(docs.filter(d => d.id !== id));
    showToast("Document removed", "error");
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 24, right: 24, zIndex: 999, padding: "12px 20px", borderRadius: 8, fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: toast.type === "success" ? "#0F2D51" : toast.type === "warning" ? "#92400e" : "#991b1b", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: 8 }}>
          {toast.type === "success" ? "✓" : "!"} {toast.msg}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Total Documents", value: docs.length, icon: "📄", color: "#0F2D51" },
          { label: "Current", value: docs.filter(d => d.status === "Current").length, icon: "✅", color: "#166534" },
          { label: "OFFICIAL: Sensitive", value: docs.filter(d => d.classification === "OFFICIAL: Sensitive").length, icon: "🔶", color: "#92400e" },
          { label: "Archived", value: docs.filter(d => d.status === "Archived").length, icon: "📦", color: "#6B7280" },
        ].map(s => (
          <div key={s.label} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 24 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: 14 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents or owners..." style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box" }} />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ padding: "9px 14px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "#fff", cursor: "pointer" }}>
          <option value="All">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)} style={{ padding: "9px 14px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "#fff", cursor: "pointer" }}>
          <option value="All">All Classifications</option>
          {CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
          <thead>
            <tr style={{ background: "#0F2D51", color: "#fff" }}>
              {["Document Title", "Version", "Owner", "Classification", "Category", "Last Updated", "Status", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc, i) => (
              <tr key={doc.id} style={{ background: i % 2 === 0 ? "#fff" : "#F9FAFB", borderBottom: "1px solid #F3F4F6", opacity: doc.status === "Archived" ? 0.5 : 1 }}>
                {editingId === doc.id ? (
                  <>
                    <td style={{ padding: "8px 14px" }}><input value={editBuf.title} onChange={e => setEditBuf({ ...editBuf, title: e.target.value })} style={{ width: "100%", padding: "5px 8px", border: "1px solid #29E2BF", borderRadius: 6, fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: "none" }} /></td>
                    <td style={{ padding: "8px 14px" }}><input value={editBuf.version} onChange={e => setEditBuf({ ...editBuf, version: e.target.value })} style={{ width: 70, padding: "5px 8px", border: "1px solid #29E2BF", borderRadius: 6, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none" }} /></td>
                    <td style={{ padding: "8px 14px" }}><input value={editBuf.owner} onChange={e => setEditBuf({ ...editBuf, owner: e.target.value })} style={{ width: 80, padding: "5px 8px", border: "1px solid #29E2BF", borderRadius: 6, fontSize: 13, outline: "none" }} /></td>
                    <td style={{ padding: "8px 14px" }}><select value={editBuf.classification} onChange={e => setEditBuf({ ...editBuf, classification: e.target.value })} style={{ padding: "5px 8px", border: "1px solid #29E2BF", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>{CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}</select></td>
                    <td style={{ padding: "8px 14px" }}><select value={editBuf.category} onChange={e => setEditBuf({ ...editBuf, category: e.target.value })} style={{ padding: "5px 8px", border: "1px solid #29E2BF", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></td>
                    <td style={{ padding: "8px 14px", color: "#9CA3AF", fontSize: 12 }}>{doc.updated}</td>
                    <td style={{ padding: "8px 14px" }}><span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#dcfce7", color: "#166534" }}>Current</span></td>
                    <td style={{ padding: "8px 14px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={saveEdit} style={{ padding: "5px 12px", background: "#0F2D51", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ padding: "5px 10px", background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>✕</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: "#111827" }}>{doc.title}</td>
                    <td style={{ padding: "10px 14px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6B7280" }}>{doc.version}</td>
                    <td style={{ padding: "10px 14px", color: "#374151" }}>{doc.owner}</td>
                    <td style={{ padding: "10px 14px" }}><ClassBadge c={doc.classification} /></td>
                    <td style={{ padding: "10px 14px", color: "#374151", fontSize: 12 }}>{doc.category}</td>
                    <td style={{ padding: "10px 14px", color: "#9CA3AF", fontSize: 12 }}>{doc.updated}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: doc.status === "Archived" ? "#F3F4F6" : "#dcfce7", color: doc.status === "Archived" ? "#6B7280" : "#166534" }}>{doc.status}</span>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => startEdit(doc)} title="Edit" style={{ padding: "5px 10px", background: "#EEF2FF", color: "#3730A3", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>✏️ Edit</button>
                        <button onClick={() => archiveDoc(doc.id)} title="Archive" style={{ padding: "5px 10px", background: "#FFF7ED", color: "#92400E", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>📦</button>
                        <button onClick={() => deleteDoc(doc.id)} title="Delete" style={{ padding: "5px 10px", background: "#FEF2F2", color: "#991B1B", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>🗑️</button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#9CA3AF", fontStyle: "italic" }}>No documents match your filters</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#9CA3AF", fontFamily: "'DM Sans', sans-serif" }}>
        Showing {filtered.length} of {docs.length} documents
      </div>
    </div>
  );
}

// ─── Page 2: Upload Document ──────────────────────────────────────────────────
function UploadDocument({ docs, setDocs, setAdminTab }) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", version: "", owner: "", classification: "OFFICIAL", category: "HR & People", supersedes: "" });
  const [step, setStep] = useState("drop"); // drop | form | processing | done
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState("");
  const fileRef = useRef();

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); setForm(prev => ({ ...prev, title: f.name.replace(/\.[^.]+$/, "") })); setStep("form"); }
  }, []);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setForm(prev => ({ ...prev, title: f.name.replace(/\.[^.]+$/, "") })); setStep("form"); }
  };

  const processDoc = () => {
    if (!form.title) return;
    setStep("processing"); setProgress(0);
    const stages = [
      { p: 20, msg: "Extracting text content..." },
      { p: 45, msg: "Chunking for vector store..." },
      { p: 65, msg: "Generating embeddings..." },
      { p: 85, msg: "Building AI summary..." },
      { p: 100, msg: "Complete" },
    ];
    let i = 0;
    const tick = () => {
      if (i < stages.length) {
        setProgress(stages[i].p);
        i++;
        setTimeout(tick, 600);
      } else {
        setSummary(`This document establishes ${form.category.toLowerCase()} policy requirements for APS staff. It covers key obligations, compliance requirements and procedural guidance. Classification: ${form.classification}. Version ${form.version || "v1.0"} supersedes ${form.supersedes || "no prior version"}.`);
        setStep("done");
      }
    };
    setTimeout(tick, 400);
  };

  const confirmAdd = () => {
    const newDoc = {
      id: Date.now(), title: form.title, version: form.version || "v1.0", owner: form.owner || "Admin",
      classification: form.classification, category: form.category,
      updated: new Date().toISOString().split("T")[0], status: "Current",
      size: file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : "—"
    };
    setDocs([...docs, newDoc]);
    setAdminTab("dashboard");
  };

  const reset = () => { setFile(null); setForm({ title: "", version: "", owner: "", classification: "OFFICIAL", category: "HR & People", supersedes: "" }); setStep("drop"); setProgress(0); setSummary(""); };

  const inp = (field, label, placeholder, mono = false) => (
    <div>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{label}</label>
      <input value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })} placeholder={placeholder}
        style={{ width: "100%", padding: "9px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: mono ? "'DM Mono', monospace" : "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box" }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      {step === "drop" && (
        <div onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={handleDrop}
          onClick={() => fileRef.current.click()}
          style={{ border: `2px dashed ${dragging ? "#29E2BF" : "#D1D5DB"}`, borderRadius: 16, padding: "64px 40px", textAlign: "center", cursor: "pointer", background: dragging ? "#F0FDFB" : "#FAFAFA", transition: "all 0.2s" }}>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.html" style={{ display: "none" }} onChange={handleFileSelect} />
          <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0F2D51", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>Drop your document here</div>
          <div style={{ fontSize: 13, color: "#9CA3AF", fontFamily: "'DM Sans', sans-serif" }}>PDF, Word, HTML or plain text · or click to browse</div>
          <div style={{ marginTop: 20, display: "inline-block", padding: "10px 24px", background: "#0F2D51", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>Browse Files</div>
        </div>
      )}

      {step === "form" && (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ background: "#0F2D51", padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>📄</span>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>{file?.name}</div>
              <div style={{ color: "#29E2BF", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{file?.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : "—"} · Ready to process</div>
            </div>
            <button onClick={reset} style={{ marginLeft: "auto", background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}>Change file</button>
          </div>
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ gridColumn: "1 / -1" }}>{inp("title", "Document Title *", "e.g. APS Code of Conduct")}</div>
            {inp("version", "Version", "e.g. v4.2", true)}
            {inp("owner", "Document Owner", "e.g. APSC")}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>Classification *</label>
              <select value={form.classification} onChange={e => setForm({ ...form, classification: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "#fff", cursor: "pointer", outline: "none" }}>
                {CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>Category *</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "#fff", cursor: "pointer", outline: "none" }}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>{inp("supersedes", "Supersedes (optional)", "e.g. APS Code of Conduct v4.1")}</div>
          </div>
          <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 10 }}>
            <button onClick={processDoc} style={{ padding: "11px 28px", background: "#0F2D51", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              ⚡ Process Document
            </button>
            <button onClick={reset} style={{ padding: "11px 20px", background: "#fff", color: "#374151", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Cancel</button>
          </div>
        </div>
      )}

      {step === "processing" && (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 48, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>⚙️</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0F2D51", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>Processing Document</div>
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 32, fontFamily: "'DM Sans', sans-serif" }}>Extracting, chunking and embedding into the AUSOVRN™ vector store...</div>
          <div style={{ background: "#F3F4F6", borderRadius: 8, height: 10, overflow: "hidden", maxWidth: 400, margin: "0 auto 12px" }}>
            <div style={{ height: "100%", background: "linear-gradient(90deg, #0F2D51, #29E2BF)", borderRadius: 8, width: `${progress}%`, transition: "width 0.5s ease" }} />
          </div>
          <div style={{ fontSize: 13, fontFamily: "'DM Mono', monospace", color: "#29E2BF" }}>{progress}% complete</div>
        </div>
      )}

      {step === "done" && (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ background: "#0F2D51", padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, fontFamily: "'DM Sans', sans-serif" }}>Document Processed Successfully</div>
            <ClassBadge c={form.classification} />
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              {[["Title", form.title], ["Version", form.version || "v1.0"], ["Category", form.category], ["Owner", form.owner || "Admin"], ["Classification", form.classification], ["Supersedes", form.supersedes || "—"]].map(([k, v]) => (
                <div key={k} style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2, fontFamily: k === "Version" ? "'DM Mono', monospace" : "'DM Sans', sans-serif" }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ background: "#F0FDFB", border: "1px solid #99F6E4", borderRadius: 8, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#0F6E56", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>🤖 AI-Generated Summary</div>
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>{summary}</div>
            </div>
          </div>
          <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 10 }}>
            <button onClick={confirmAdd} style={{ padding: "11px 28px", background: "#0F2D51", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              ✓ Add to Document Store
            </button>
            <button onClick={reset} style={{ padding: "11px 20px", background: "#fff", color: "#374151", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Upload Another</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page 3: Folder Import ────────────────────────────────────────────────────
function FolderImport({ docs, setDocs, setAdminTab }) {
  const [tree, setTree] = useState(FOLDER_TREE);
  const [selected, setSelected] = useState({});
  const [step, setStep] = useState("browse"); // browse | classify | importing | done
  const [bulkClass, setBulkClass] = useState("OFFICIAL");
  const [bulkCat, setBulkCat] = useState("HR & People");
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState("");

  const toggleFolder = (id) => setTree(tree.map(f => f.id === id ? { ...f, expanded: !f.expanded } : f));

  const allFiles = tree.flatMap(f => f.children.map(c => ({ folder: f.name, file: c, key: `${f.id}/${c}` })));
  const selectedFiles = allFiles.filter(f => selected[f.key]);
  const selectedCount = selectedFiles.length;

  const toggleFile = (key) => setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  const selectAll = () => { const all = {}; allFiles.forEach(f => all[f.key] = true); setSelected(all); };
  const clearAll = () => setSelected({});

  const startImport = () => {
    setStep("importing"); setProgress(0);
    let i = 0;
    const tick = () => {
      if (i < selectedFiles.length) {
        setCurrentFile(selectedFiles[i].file);
        setProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
        i++;
        setTimeout(tick, 400);
      } else { setStep("done"); }
    };
    setTimeout(tick, 300);
  };

  const confirmImport = () => {
    const newDocs = selectedFiles.map((f, i) => ({
      id: Date.now() + i,
      title: f.file.replace(/\.[^.]+$/, ""),
      version: "v1.0",
      owner: "Imported",
      classification: bulkClass,
      category: bulkCat,
      updated: new Date().toISOString().split("T")[0],
      status: "Current",
      size: "—"
    }));
    setDocs([...docs, ...newDocs]);
    setAdminTab("dashboard");
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {step === "browse" && (
        <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 20 }}>
          {/* Folder tree */}
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ background: "#0F2D51", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>📁 Agency Document Store</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={selectAll} style={{ fontSize: 11, color: "#29E2BF", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>All</button>
                <button onClick={clearAll} style={{ fontSize: 11, color: "#9CA3AF", background: "none", border: "none", cursor: "pointer" }}>None</button>
              </div>
            </div>
            <div style={{ padding: 8 }}>
              {tree.map(folder => (
                <div key={folder.id}>
                  <div onClick={() => toggleFolder(folder.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, cursor: "pointer", userSelect: "none" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ fontSize: 10, color: "#9CA3AF", transition: "transform 0.15s", display: "inline-block", transform: folder.expanded ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                    <span>{folder.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", fontFamily: "'DM Sans', sans-serif" }}>{folder.name}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "#9CA3AF", fontFamily: "'DM Mono', monospace" }}>{folder.children.length}</span>
                  </div>
                  {folder.expanded && folder.children.map(child => {
                    const key = `${folder.id}/${child}`;
                    return (
                      <div key={key} onClick={() => toggleFile(key)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px 7px 34px", cursor: "pointer", borderRadius: 6, background: selected[key] ? "#EFF6FF" : "transparent" }}
                        onMouseEnter={e => { if (!selected[key]) e.currentTarget.style.background = "#F9FAFB"; }} onMouseLeave={e => { if (!selected[key]) e.currentTarget.style.background = "transparent"; }}>
                        <input type="checkbox" checked={!!selected[key]} readOnly style={{ accentColor: "#0F2D51", cursor: "pointer" }} />
                        <span style={{ fontSize: 12 }}>📄</span>
                        <span style={{ fontSize: 12, color: "#374151", fontFamily: "'DM Sans', sans-serif" }}>{child}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Selection summary + classify */}
          <div>
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0F2D51", marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>
                {selectedCount === 0 ? "Select files from the folder tree" : `${selectedCount} file${selectedCount !== 1 ? "s" : ""} selected`}
              </div>
              {selectedCount > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                  {selectedFiles.map(f => (
                    <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", background: "#F9FAFB", borderRadius: 6, fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                      <span>📄</span>
                      <span style={{ color: "#374151" }}>{f.file}</span>
                      <span style={{ color: "#9CA3AF", marginLeft: "auto", fontSize: 11 }}>{f.folder}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedCount > 0 && (
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>Bulk Classification Wizard</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>Apply Classification to All</label>
                    <select value={bulkClass} onChange={e => setBulkClass(e.target.value)} style={{ width: "100%", padding: "9px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "#fff", cursor: "pointer", outline: "none" }}>
                      {CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>Apply Category to All</label>
                    <select value={bulkCat} onChange={e => setBulkCat(e.target.value)} style={{ width: "100%", padding: "9px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: "#fff", cursor: "pointer", outline: "none" }}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ background: "#F0FDFB", border: "1px solid #99F6E4", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#0F6E56", fontFamily: "'DM Sans', sans-serif" }}>
                  ℹ️ All {selectedCount} selected files will be imported as <strong>{bulkClass}</strong> · <strong>{bulkCat}</strong>. You can edit individual documents after import.
                </div>
                <button onClick={startImport} style={{ padding: "11px 28px", background: "#0F2D51", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  ⚡ Import {selectedCount} Document{selectedCount !== 1 ? "s" : ""}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {step === "importing" && (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 48, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>📥</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0F2D51", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>Importing Documents</div>
          <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 8, fontFamily: "'DM Mono', monospace" }}>{currentFile}</div>
          <div style={{ background: "#F3F4F6", borderRadius: 8, height: 10, overflow: "hidden", maxWidth: 400, margin: "0 auto 12px" }}>
            <div style={{ height: "100%", background: "linear-gradient(90deg, #0F2D51, #29E2BF)", borderRadius: 8, width: `${progress}%`, transition: "width 0.35s ease" }} />
          </div>
          <div style={{ fontSize: 13, fontFamily: "'DM Mono', monospace", color: "#29E2BF" }}>{progress}% · {Math.round((progress / 100) * selectedFiles.length)}/{selectedFiles.length} files</div>
        </div>
      )}

      {step === "done" && (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ background: "#0F2D51", padding: "20px 28px" }}>
            <div style={{ color: "#29E2BF", fontSize: 28, marginBottom: 4 }}>✓</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, fontFamily: "'DM Sans', sans-serif" }}>Import Complete</div>
            <div style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>{selectedFiles.length} documents added to the AUSOVRN™ Assist document store</div>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              {[["Documents Imported", selectedFiles.length], ["Classification", bulkClass], ["Category", bulkCat]].map(([k, v]) => (
                <div key={k} style={{ background: "#F9FAFB", borderRadius: 8, padding: "12px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>{k}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0F2D51", marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={confirmImport} style={{ padding: "11px 28px", background: "#0F2D51", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                ✓ View Document Store
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Admin Portal ────────────────────────────────────────────────────────
export default function AdminPortal({ onNavigate }) {
  const [activeTab, setAdminTab] = useState("dashboard");
  const [docs, setDocs] = useState(INITIAL_DOCS);

  const tabs = [
    { id: "dashboard", label: "Document Dashboard", icon: "📋", count: docs.length },
    { id: "upload", label: "Upload Document", icon: "⬆️" },
    { id: "import", label: "Folder Import", icon: "📁" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#F3F4F6", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ background: "#0F2D51", borderBottom: "3px solid #29E2BF" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 32, height: 32, background: "#29E2BF", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🛡️</div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>AUSOVRN™ Assist</div>
                  <div style={{ color: "#29E2BF", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>Admin Portal</div>
                </div>
              </div>
              <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.15)" }} />
              <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'DM Mono', monospace" }}>CAN.B Group · OFFICIAL · Sovereign Environment</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E" }} />
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>NEXTDC C1 Canberra</span>
              <div style={{ width: 32, height: 32, background: "rgba(255,255,255,0.1)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: "pointer" }}>👤</div>
              {onNavigate && (
                <button onClick={() => onNavigate("chat")} style={{ padding: "6px 14px", background: "rgba(41,226,191,0.15)", border: "1px solid rgba(41,226,191,0.4)", borderRadius: 8, color: "#29E2BF", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  ← Back to App
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 2 }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setAdminTab(tab.id)}
                style={{ padding: "12px 20px", background: activeTab === tab.id ? "#fff" : "transparent", color: activeTab === tab.id ? "#0F2D51" : "rgba(255,255,255,0.6)", border: "none", borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s" }}>
                <span>{tab.icon}</span>
                {tab.label}
                {tab.count !== undefined && (
                  <span style={{ background: activeTab === tab.id ? "#0F2D51" : "rgba(255,255,255,0.2)", color: activeTab === tab.id ? "#fff" : "#fff", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 10, fontFamily: "'DM Mono', monospace" }}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 24px" }}>
        {/* Page header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0F2D51", letterSpacing: "-0.02em" }}>
            {tabs.find(t => t.id === activeTab)?.icon} {tabs.find(t => t.id === activeTab)?.label}
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6B7280" }}>
            {activeTab === "dashboard" && "Manage, edit and monitor your agency document store"}
            {activeTab === "upload" && "Upload and process a new document into the AUSOVRN™ vector store"}
            {activeTab === "import" && "Import an entire folder structure with bulk classification"}
          </p>
        </div>

        {activeTab === "dashboard" && <DocumentDashboard docs={docs} setDocs={setDocs} />}
        {activeTab === "upload" && <UploadDocument docs={docs} setDocs={setDocs} setAdminTab={setAdminTab} />}
        {activeTab === "import" && <FolderImport docs={docs} setDocs={setDocs} setAdminTab={setAdminTab} />}
      </div>
    </div>
  );
}
